import React, { useState, useRef } from 'react';
import { Modal, Box, Button, Typography, Tabs, Tab, Tooltip } from '@mui/material';
import { useSendEmail, type EmailStatus } from '../hooks/useSendEmail';
import type { SendEmailModalProps } from '../hooks/useSendEmailModal';
import { EmailDetails } from './EmailDetails';
import { LoadingButton } from '@mui/lab';
import { CustomdateRangePicker } from './CustomdateRangePicker';
import EmailResponseSummary from './EmailResponseSummary';
import EmailTemplatePreview from './EmailTemplatePreview';
import EmailSendingModal from './EmailSendingModal';
import { showWarningSnackbar, showSuccessSnackbar } from '../../../components/snackBar/snakbar';
import { DateRangeRsuite } from '../../../components';
import dayjs from 'dayjs';
import { formatUnixTimestamp } from '../../../library';

export const SendEmailModalWrapper: React.FC<SendEmailModalProps> = ({
  open,
  onClose,
  initialPageType = '',
  recipients = [],
  onUpdateEmail,
  isEditRecipient,
  setIsEditRecipient,
  removeRecipient,
}) => {
  
  const getTemplateIdByPageType = (pageType: string): number => {
    const pageTypeLower = pageType.toLowerCase();
    
    if (pageTypeLower.includes('payment') || pageTypeLower.includes('payment confirmation')) {
      return 4;
    } else if (pageTypeLower.includes('purchase')) {
      return 3;
    } else if (pageTypeLower.includes('invoice') || pageTypeLower.includes('sales')) {
      return 1;
    } else if (pageTypeLower.includes('reminder')) {
      return 2;
    } else if (pageTypeLower.includes('debit')) {
      return 5;
    } else if (pageTypeLower.includes('credit')) {
      return 6;
    } else if (pageTypeLower.includes('receipt')) {
      return 2;
    } else if (pageTypeLower.includes('customer')) {
      return 7;
    } else if (pageTypeLower.includes('supplier')) {
      return 7;
    } else if (pageTypeLower.includes('pos')) {
      return 1;
    }
    
    return 1;
  };
  const [activeTab, setActiveTab] = useState(0);
  const [isTemplateSaved, setIsTemplateSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [getCurrentTemplateContent, setGetCurrentTemplateContent] = useState<(() => string) | null>(null);
  const [saveTemplateFunction, setSaveTemplateFunction] = useState<(() => Promise<void>) | null>(null);
  const [showUnsavedChangesPopup, setShowUnsavedChangesPopup] = useState<(() => boolean) | null>(null);
  const [emailSendingProgress, setEmailSendingProgress] = useState({
    sentEmails: 0,
    totalEmails: 0,
    message: 'Preparing to send emails...',
    progressPercentage: 0,
  });

  const {
    loading,
    pageType,
    dateRange,
    handleDateRangeChange,
    modalHeader,
    handleSendEmail,
    handleCloseResponseSummary,
    responseSummary,
    startdate,
    enddate,
    handleCloseWrapperModal,
  } = useSendEmail({
    initialPageType,
    recipients,
    onClose,
    onProgress: (progress) => {
      setEmailSendingProgress(progress);
    },
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSaveTemplate = (templateData: any) => {
    // Handle template save if needed
  };

  const handleTemplateSaveStatusChange = (saved: boolean) => {
    setIsTemplateSaved(saved);
  };

  const handleUnsavedChangesChange = (hasChanges: boolean) => {
    setHasUnsavedChanges(hasChanges);
  };

  const handleGetCurrentTemplateContent = (getContent: () => string) => {
    setGetCurrentTemplateContent(() => getContent);
  };

  const handleGetSaveFunction = (saveFunction: () => Promise<void>) => {
    setSaveTemplateFunction(() => saveFunction);
  };

  const handleGetShowUnsavedChangesPopup = (showFunction: () => boolean) => {
    setShowUnsavedChangesPopup(() => showFunction);
  };

  const handleConfirmTemplateUpdate = () => {
    setActiveTab(1);
  };

  const handleCancelTemplateUpdate = () => {
    showWarningSnackbar('Template changes have been discarded. The original template content will be used.');
  };

  const handleNextClick = async () => {
    if (hasUnsavedChanges && showUnsavedChangesPopup) {
      const popupShown = showUnsavedChangesPopup();
      if (popupShown) {
        return;
      }
    }
    
    if (!isTemplateSaved) {
      showWarningSnackbar('Please save the email template before proceeding to recipients tab');
      return;
    }
    setActiveTab(1);
  };

  const handleSendClick = async () => {
    if (hasUnsavedChanges && showUnsavedChangesPopup) {
      const popupShown = showUnsavedChangesPopup();
      if (popupShown) {
        return;
      }
    }
    
    if (!isTemplateSaved) {
      showWarningSnackbar('Please save the email template before sending emails');
      return;
    }
    
    // Reset progress state
    setEmailSendingProgress({
      sentEmails: 0,
      totalEmails: recipients.length,
      message: 'Preparing to send emails...',
      progressPercentage: 0,
    });
    
    // Call the email sending function - progress will be handled by the hook
    handleSendEmail();
  };

  




  return (
    <>
      <Modal open={open} aria-labelledby="send-email-modal">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { 
              xs: '98vw', 
              sm: '95vw', 
              md: '90vw', 
              lg: '80vw',
              xl: '70vw'
            },
            maxWidth: { xs: 'none', sm: 1000, md: 1200, lg: 1400 },
            height: {
              xs: '98vh',
              sm: '95vh',
              md: '92vh',
              lg: '88vh'
            },
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: { xs: 1, sm: 2 },
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            overflow: 'hidden',
          }}
        >
          {/* Sticky Header */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 3,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              p: { xs: 1.5, sm: 2 },
              boxShadow: 2,
            }}
          >
            <Box 
              component="h2" 
              id="send-email-modal" 
              sx={{ 
                m: 0, 
                fontSize: { xs: '14px', sm: '16px', md: '18px' },
                fontWeight: 600,
              }}
            >
              {modalHeader}
            </Box>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              aria-label="email modal tabs"
              variant={window.innerWidth < 600 ? 'scrollable' : 'standard'}
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: { xs: '12px', sm: '14px' },
                  minHeight: { xs: 40, sm: 48 },
                  px: { xs: 1, sm: 2 },
                },
              }}
            >
              <Tab label="Email Template Preview" />
              <Tab label="Recipients" />
            </Tabs>
          </Box>

          {/* Sticky Date Range */}
          {(pageType === 'customer' || pageType === 'supplier') && activeTab === 1 && (
            <Box
              sx={{
                position: 'sticky',
                top: { xs: 88, sm: 104 },
                zIndex: 2,
                bgcolor: 'background.paper',
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1, sm: 1.5 },
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontSize: { xs: '13px', sm: '15px' },
                  mb: { xs: 0.5, sm: 1 },
                }}
              >
                * Select Date Range{' '}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 2 }}>
                <DateRangeRsuite value={[dateRange.from, dateRange.to]} onChange={handleDateRangeChange} showHeader  />
                <Typography>
                  <Box sx={{ ml: 1, fontSize: '14px', color: 'text.secondary' }}>
                    {dateRange.from && dateRange.to ? `${formatUnixTimestamp(dateRange.from)} to ${formatUnixTimestamp(dateRange.to)}` : 'Please select a date range'}
                    
                  </Box>
                </Typography>
              </Box>
            </Box>
          )}
          
          {/* Tab Content */}
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto', 
            p: activeTab === 0 ? { xs: 0.5, sm: 0.75 } : 0,
            minHeight: 0,
          }}>
            {activeTab === 0 && (
              <EmailTemplatePreview
                templateId={getTemplateIdByPageType(initialPageType)}
                onSaveTemplate={handleSaveTemplate}
                onTemplateSaveStatusChange={handleTemplateSaveStatusChange}
                onUnsavedChangesChange={handleUnsavedChangesChange}
                onGetCurrentTemplateContent={handleGetCurrentTemplateContent}
                onGetSaveFunction={handleGetSaveFunction}
                onGetShowUnsavedChangesPopup={handleGetShowUnsavedChangesPopup}
                onConfirmTemplateUpdate={handleConfirmTemplateUpdate}
                onCancelTemplateUpdate={handleCancelTemplateUpdate}
              />
            )}
            
            {activeTab === 1 && (
              <Box sx={{ py: 0.2 }}>
                <EmailDetails
                  recipients={recipients}
                  onUpdateEmail={onUpdateEmail}
                  setIsEditRecipient={setIsEditRecipient}
                  removeRecipient={removeRecipient}
                  addVoucherCol={pageType !== 'customer' && pageType !== 'supplier'}
                />
              </Box>
            )}
          </Box>

          {/* Sticky Actions */}
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 3,
              bgcolor: 'background.paper',
              boxShadow: 3,
              py: { xs: 1.5, sm: 2 },
              px: { xs: 1.5, sm: 2 },
              display: 'flex',
              justifyContent: 'flex-end',
              gap: { xs: 1, sm: 2 },
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            {activeTab === 0 ? (
              <>
                <Button 
                  variant="outlined" 
                  onClick={handleCloseWrapperModal} 
                  sx={{ 
                    minWidth: { xs: '100%', sm: 100 },
                    order: { xs: 2, sm: 1 },
                  }}
                >
                  Close
                </Button>
                <Tooltip 
                  title={hasUnsavedChanges ? "You have unsaved changes. They will be saved automatically when you proceed." : ""} 
                  arrow
                >
                  <Button 
                    variant="contained" 
                    onClick={handleNextClick} 
                    sx={{ 
                      minWidth: { xs: '100%', sm: 100 },
                      order: { xs: 1, sm: 2 },
                      position: 'relative',
                    }}
                  >
                    Next
                    {hasUnsavedChanges && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'warning.main',
                          border: '1px solid white',
                        }}
                      />
                    )}
                  </Button>
                </Tooltip>
              </>
            ) : (
              <>
                <Button 
                  variant="outlined" 
                  onClick={handleCloseWrapperModal} 
                  sx={{ 
                    minWidth: { xs: '100%', sm: 100 },
                    order: { xs: 2, sm: 1 },
                  }}
                >
                  Close
                </Button>
                <Tooltip 
                  title={hasUnsavedChanges ? "You have unsaved changes. They will be saved automatically when you send." : ""} 
                  arrow
                >
                  <LoadingButton
                    variant="contained"
                    onClick={handleSendClick}
                    loading={loading}
                    disabled={isEditRecipient !== null}
                    sx={{ 
                      minWidth: { xs: '100%', sm: 140 },
                      order: { xs: 1, sm: 2 },
                      position: 'relative',
                    }}
                  >
                    Send
                    {hasUnsavedChanges && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'warning.main',
                          border: '1px solid white',
                        }}
                      />
                    )}
                  </LoadingButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>
      </Modal>
      {responseSummary?.open && (
        <EmailResponseSummary
          open={responseSummary?.open}
          onClose={() => handleCloseResponseSummary()}
          data={responseSummary?.data?.data || null}
          addVoucherCol={pageType !== 'customer' && pageType !== 'supplier'}
        />
      )}
      
      {/* Email Sending Loading Modal */}
      <EmailSendingModal
        open={loading}
        progress={emailSendingProgress.progressPercentage}
        message={emailSendingProgress.message}
        totalEmails={emailSendingProgress.totalEmails}
        sentEmails={emailSendingProgress.sentEmails}
      />
    </>
  );
};

export default SendEmailModalWrapper;
